// U-04 fase a2 · LA COLA SIN RED DE LA CAPTURA EN SERIE (DISEÑO-U-04 §6.3).
//
// Una captura que no llega al servidor se queda en este teléfono y se envía
// sola al volver la señal, EN ORDEN y con el MISMO `id_operacion` con el que
// nació (prueba 37). Ese id es lo que hace seguro reenviar: el servidor
// guarda una fila por id y empresa (`uq_fila_operacion`), y si el mismo id
// llega otra vez con el mismo cuerpo contesta 200 con la fila que ya tenía.
// Generar un id nuevo en cada intento convertiría «la respuesta se perdió»
// en una captura duplicada, que es justo lo que se quiere evitar.
//
// POR QUÉ localStorage Y NO IndexedDB como las otras colas del teléfono:
// el diseño la pide así (por empresa y migración) y lo que guarda es poco y
// pequeño —una línea por producto capturado—. Lo que importa es la misma
// forma que `colaRecepciones.js`: se guarda ANTES de intentar el envío, así
// que cerrar el navegador entre el envío y la respuesta no pierde nada.
//
// LO QUE EL SERVIDOR RECHAZA NO SE TRAGA. Un 409 («ya se recibió con otros
// datos», «la migración ya se cerró») o un 422 no se arreglan reintentando:
// sale de la cola de envío para no atascarla, pasa a `rechazadas` con el
// texto del servidor, y la pantalla lo enseña hasta que la persona lo quita.
import { api, ErrorApi } from '../api';
import { uuidv7 } from '../lib/uuidv7';

const PREFIJO = 'tego.migracion.captura';

function empresaDe(usuario) {
  const e = usuario?.id_corporacion ?? usuario?.id_empresa;
  return e === undefined || e === null || e === '' ? '-' : String(e);
}

/** La llave de la cola de esta persona en esta migración, o `null`. */
export function llaveCola(usuario, idMigracion) {
  const id = usuario?.id;
  if (id === undefined || id === null || id === '' || idMigracion === undefined || idMigracion === null) return null;
  return `${PREFIJO}:${empresaDe(usuario)}:${id}:${idMigracion}`;
}

function leer(llave) {
  if (!llave) return { pendientes: [], rechazadas: [] };
  try {
    const v = JSON.parse(globalThis.localStorage.getItem(llave) || 'null');
    return {
      pendientes: Array.isArray(v?.pendientes) ? v.pendientes : [],
      rechazadas: Array.isArray(v?.rechazadas) ? v.rechazadas : [],
    };
  } catch {
    return { pendientes: [], rechazadas: [] };
  }
}

// NUNCA LANZA. Una ventana privada o un almacén lleno hacen fallar estas
// llamadas; quien llama se entera por el `false`.
function escribir(llave, estado) {
  if (!llave) return false;
  try {
    if (!estado.pendientes.length && !estado.rechazadas.length) globalThis.localStorage.removeItem(llave);
    else globalThis.localStorage.setItem(llave, JSON.stringify(estado));
    return true;
  } catch {
    return false;
  }
}

export const pendientesCaptura = (llave) => leer(llave).pendientes;
export const rechazadasCaptura = (llave) => leer(llave).rechazadas;
export const contarPendientesCaptura = (llave) => leer(llave).pendientes.length;

function quitarPendiente(llave, idOperacion) {
  const e = leer(llave);
  escribir(llave, { ...e, pendientes: e.pendientes.filter((c) => c.id_operacion !== idOperacion) });
}

function pasarARechazadas(llave, captura, motivo) {
  const e = leer(llave);
  escribir(llave, {
    pendientes: e.pendientes.filter((c) => c.id_operacion !== captura.id_operacion),
    rechazadas: [...e.rechazadas.filter((c) => c.id_operacion !== captura.id_operacion), { ...captura, motivo }],
  });
}

/** La persona ya leyó el rechazo y lo quita de la lista. */
export function descartarRechazada(llave, idOperacion) {
  const e = leer(llave);
  escribir(llave, { ...e, rechazadas: e.rechazadas.filter((c) => c.id_operacion !== idOperacion) });
}

/** Un 4xx de regla de negocio: reintentar no lo arregla. */
export function esDefinitivo(e) {
  return e instanceof ErrorApi && [400, 403, 404, 409, 422].includes(e.status);
}

// Lo que va al servidor: sin los campos internos de la cola.
function cuerpoDe(captura) {
  const { _ts, motivo, ...resto } = captura;
  void _ts; void motivo;
  return resto;
}

const enviar = (idMigracion, captura) => api.post(`/migracion/${idMigracion}/captura`, cuerpoDe(captura));

/**
 * Captura una línea. Devuelve
 *   {estado: 'enviado', fila}   — llegó al servidor
 *   {estado: 'encolado'}        — sin señal o sin respuesta; queda en el teléfono
 * y lanza el `ErrorApi` si el servidor la rechaza por una regla (no se encola).
 */
export async function capturar(llave, idMigracion, datos) {
  const captura = { ...datos, id_operacion: uuidv7(), _ts: Date.now() };
  const e = leer(llave);
  const guardada = escribir(llave, { ...e, pendientes: [...e.pendientes, captura] });
  if (globalThis.navigator?.onLine === false) {
    if (!guardada) {
      throw new Error('Este teléfono no deja guardar capturas sin conexión (el almacenamiento del navegador está '
        + 'bloqueado o lleno). Captura este producto cuando vuelva la señal.');
    }
    return { estado: 'encolado', captura };
  }
  try {
    const fila = await enviar(idMigracion, captura);
    quitarPendiente(llave, captura.id_operacion);
    return { estado: 'enviado', fila, captura };
  } catch (err) {
    if (esDefinitivo(err)) {
      quitarPendiente(llave, captura.id_operacion);
      throw err;
    }
    // Red caída o servidor sin contestar. Si la petición SÍ llegó y lo que se
    // perdió fue la respuesta, el reenvío con el mismo id no duplica nada.
    if (!guardada) throw err;
    return { estado: 'encolado', captura };
  }
}

const enCurso = new Set();

/**
 * Reenvía lo pendiente, en el orden en que se capturó. Se detiene al primer
 * fallo de red o del servidor: lo que sigue espera su turno, para no
 * adelantar una captura a otra.
 * Devuelve {enviados, rechazadas: [captura con motivo], restantes}.
 */
export async function reenviar(llave, idMigracion) {
  const restantes = () => contarPendientesCaptura(llave);
  if (!llave || enCurso.has(llave) || globalThis.navigator?.onLine === false) {
    return { enviados: 0, rechazadas: [], restantes: restantes() };
  }
  enCurso.add(llave);
  let enviados = 0;
  const rechazadas = [];
  try {
    const orden = [...pendientesCaptura(llave)].sort((a, b) => (a._ts || 0) - (b._ts || 0));
    for (const captura of orden) {
      try {
        await enviar(idMigracion, captura);
        quitarPendiente(llave, captura.id_operacion);
        enviados += 1;
      } catch (err) {
        if (!esDefinitivo(err)) break;
        pasarARechazadas(llave, captura, err.message);
        rechazadas.push({ ...captura, motivo: err.message });
      }
    }
  } finally {
    enCurso.delete(llave);
  }
  return { enviados, rechazadas, restantes: restantes() };
}

// Las ubicaciones, recordadas en el teléfono: sin señal, la lista para
// elegir dónde está lo capturado sale de aquí.
const llaveUbicaciones = (usuario) => (usuario?.id === undefined || usuario?.id === null ? null
  : `${PREFIJO}.ubicaciones:${empresaDe(usuario)}:${usuario.id}`);

export function recordarUbicaciones(usuario, lista) {
  const llave = llaveUbicaciones(usuario);
  if (!llave || !Array.isArray(lista)) return;
  try {
    globalThis.localStorage.setItem(llave, JSON.stringify(lista.map((u) => ({ id: u.id, codigo: u.codigo, nombre: u.nombre }))));
  } catch { /* sin almacenamiento: se volverán a pedir */ }
}

export function ubicacionesRecordadas(usuario) {
  const llave = llaveUbicaciones(usuario);
  if (!llave) return [];
  try {
    const v = JSON.parse(globalThis.localStorage.getItem(llave) || '[]');
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}
